import {
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
} from '@dnd-kit/core';
import { toast } from 'sonner';
import type { CalendarPropertyRow } from '@/shared/api';
import { familyDropRefusal } from './calendarFamily';
import {
  reassignmentRequestFromDrag,
  type CalendarReassignmentDragData,
  type CalendarReassignmentDropData,
  type CalendarReassignmentRequest,
} from './calendarReassignment';

/**
 * Ziehen einer Buchung auf ein anderes Apartment. Ein Ziel, das der Wohnungsverbund in einer
 * sichtbaren Nacht belegt, lehnt das Ziehen selbst mit Grund ab — der Server hätte es ohnehin
 * abgelehnt; jedes andere Ziel öffnet die Vorschau, und dort entscheidet der Server.
 */
export function useCalendarDrag(
  rows: CalendarPropertyRow[],
  onRequest?: (request: CalendarReassignmentRequest) => void,
) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor),
  );
  const refusalFor = (targetPropertyId: string, drag: CalendarReassignmentDragData) => {
    const target = rows.find((row) => row.propertyId === targetPropertyId);
    const source = rows.find((row) => row.propertyId === drag.sourcePropertyId);
    return target ? familyDropRefusal(target, source, drag.span) : null;
  };
  const refusalOver = (
    active: { data: { current?: unknown } },
    over: { data: { current?: unknown } } | null,
  ) => {
    const drop = over?.data.current as CalendarReassignmentDropData | undefined;
    return drop
      ? refusalFor(drop.targetPropertyId, active.data.current as CalendarReassignmentDragData)
      : null;
  };

  const finishDrag = ({ active, over }: DragEndEvent) => {
    const request = reassignmentRequestFromDrag(
      active.data.current as CalendarReassignmentDragData,
      (over?.data.current as CalendarReassignmentDropData | undefined) ?? null,
    );
    if (!request) return;
    const refusal = refusalOver(active, over);
    if (refusal) {
      toast.error(refusal);
      return;
    }
    onRequest?.(request);
  };

  const announcements: Announcements = {
    onDragStart: () => 'Buchung aufgenommen. Ziel-Apartment auswählen.',
    onDragOver: ({ active, over }) => {
      const refusal = refusalOver(active, over);
      return refusal ? `Nicht möglich. ${refusal}` : undefined;
    },
    onDragEnd: ({ active, over }) => {
      if (!over) return 'Verschieben abgebrochen.';
      const refusal = refusalOver(active, over);
      return refusal ? `Nicht verschoben. ${refusal}` : 'Ziel gewählt. Vorschau wird geöffnet.';
    },
    onDragCancel: () => 'Verschieben abgebrochen.',
  };

  return { sensors, finishDrag, announcements, refusalFor };
}
